import React, { useState } from 'react';
import { Alert, Button, Card, Form, Input, Result, Typography } from 'antd';
import styled from 'styled-components';
import { API_BASE_URL } from '../../constants/api';

// Public page required by Google Play: explains how Deployed Hearts users can
// delete their account and data, and lets people who can't use the app send a
// deletion request. Linked from the Play Console data safety form.

const SUPPORT_EMAIL = 'appstore@deployedhearts.com';

const { Title, Paragraph, Text } = Typography;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  justify-content: center;
  padding: 32px 16px;
`;

const Content = styled.div`
  width: 100%;
  max-width: 680px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled(Card)`
  border-radius: 12px;

  ul {
    padding-left: 20px;
    margin-bottom: 0;
  }

  li {
    margin-bottom: 6px;
  }
`;

const DeleteAccount = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/public/account-deletion-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || `Please email ${SUPPORT_EMAIL} instead.`);
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || `We couldn't send your request. Please email ${SUPPORT_EMAIL}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <Content>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Delete your Deployed Hearts account</Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Deployed Hearts is the Black Heart Association app for healthcare workers.
            You can delete your account and personal data at any time.
          </Paragraph>
        </div>

        <Section title="Option 1: Delete it in the app (immediate)">
          <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
            <li>Open Deployed Hearts and sign in.</li>
            <li>Go to <Text strong>Settings</Text>.</li>
            <li>Tap <Text strong>Delete account</Text> and confirm.</li>
          </ol>
          <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
            Your account is deleted right away and you are signed out.
          </Paragraph>
        </Section>

        <Section title="Option 2: Ask us to delete it">
          {submitted ? (
            <Result
              status="success"
              title="Request received"
              subTitle={`We'll verify it's you by contacting the email on the account, then delete the account within 30 days. Questions? Email ${SUPPORT_EMAIL}.`}
              style={{ padding: '8px 0' }}
            />
          ) : (
            <>
              <Paragraph>
                Can’t sign in, or no longer have the app? Send us a request and we’ll delete your account
                within 30 days. We’ll contact you at the account email to confirm it’s you first.
                You can also email <a href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20Deployed%20Hearts%20account`}>{SUPPORT_EMAIL}</a>.
              </Paragraph>
              {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
              <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark="optional">
                <Form.Item
                  name="email"
                  label="Email address on your account"
                  rules={[
                    { required: true, message: 'Enter the email you use to sign in' },
                    { type: 'email', message: 'Enter a valid email address' }
                  ]}
                >
                  <Input autoComplete="email" maxLength={200} />
                </Form.Item>
                <Form.Item name="fullName" label="Full name">
                  <Input autoComplete="name" maxLength={120} />
                </Form.Item>
                <Form.Item name="phone" label="Phone number on your account">
                  <Input autoComplete="tel" maxLength={30} />
                </Form.Item>
                <Form.Item name="details" label="Anything else we should know?">
                  <Input.TextArea rows={3} maxLength={1000} />
                </Form.Item>
                <Button type="primary" danger htmlType="submit" loading={submitting}>
                  Request account deletion
                </Button>
              </Form>
            </>
          )}
        </Section>

        <Section title="What gets deleted">
          <ul>
            <li>Your healthcare worker profile: name, email, phone, address, professional role and experience.</li>
            <li>Documents you uploaded, such as your ID and professional license.</li>
            <li>Your notification settings and the device tokens used to send you notifications.</li>
            <li>Your sign-in for the app. If you also use the same login for the Black Heart Association
              web portal, only your Deployed Hearts profile is removed and your portal login keeps working.</li>
            <li>Any upcoming shifts you had claimed are released so other workers can take them.</li>
          </ul>
        </Section>

        <Section title="What we keep, and why">
          <ul>
            <li>Records of shifts you completed and payouts you received, which we must keep for payroll,
              tax and accounting purposes for as long as the law requires.</li>
            <li>Event records you created while working, such as kit and drop-off photos and patient screening
              entries. These belong to the health screening program’s records and are kept under our
              healthcare record-keeping obligations.</li>
            <li>Payout bank and tax details are held by our payment processor, Stripe, under its own policies.</li>
            <li>Data in routine backups and security logs is removed as those backups and logs expire.</li>
          </ul>
          <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
            See our <a href="https://blackheartassociation.org/privacy-policy" target="_blank" rel="noreferrer">privacy policy</a> for details.
          </Paragraph>
        </Section>
      </Content>
    </PageWrapper>
  );
};

export default DeleteAccount;
